<template>
  <article class="itp-used-list card-image" @click.prevent="emit('select', callInfo)">
    <div class="col-wrap">
      <div class="left-col">
        <div class="logo-box">
          <img :src="chatLogoImageUrl" alt="logo" />
        </div>
      </div>

      <div class="right-col">
        <div class="top-col flex-btw">
          <div>
            <InterpretingBadge :label="chatTypeLabel" :type="callTypeInfo.type" />
          </div>
          <div>
            <IconFavorite
              class="icon"
              :is-favorite="callInfo.isFavorite"
              @click.stop="emit('toggle-favorite', callInfo)"
            />
          </div>
        </div>

        <div class="bottom-col flex-btw">
          <div class="meta-title">
            <p class="tit">{{ callInfo.title }}</p>
          </div>
          <p class="dim date">
            {{ displayedTime }}
          </p>
        </div>
      </div>
    </div>

    <div class="col-btn-wrap" @click.stop>
      <div class="left-bw">
        <button class="s-btn" type="button" @click.stop="handleActiveModalName('share')">
          <span>{{ t('label_qr_invite') }}</span>
        </button>
        <div class="line" />
        <button
          v-if="isHistoryDownloadEnabled"
          class="s-btn"
          type="button"
          @click.stop="handleActiveModalName('download')"
        >
          <span>{{ t('download') }}</span>
        </button>
      </div>

      <button ref="ref-button-edit" type="button" class="s-btn" @click.stop="openDropdownEdit">
        <span>{{ t('label_settings') }}</span>
      </button>
    </div>

    <Teleport to="body">
      <ModalShareChat v-if="activeModalName === 'share'" :call-info="callInfo" @close="handleActiveModalName()" />

      <DownloadTableModal
        v-else-if="activeModalName === 'download'"
        :code="callInfo.code"
        :chat-type="callInfo.callType"
        @close="handleActiveModalName()"
      />
      <ModalMedium
        v-else-if="activeModalName === 'edit'"
        class="modify-modal"
        :title="t('label_edit_interpretation')"
        @close="handleActiveModalName()"
      >
        <SelfServiceChatEdit :code="callInfo.code" @updated="refreshAfterRequest" />
      </ModalMedium>

      <DropdownEdit
        v-if="isOpenDropdownEdit"
        :is-visible="isOpenDropdownEdit"
        :target-el="refButtonEdit"
        @close="closeDropdownEdit"
      >
        <button class="md-pop-btn m-btn" @click.stop="handleActiveModalName('edit')">
          <IconModify class="icon" />
          <span>{{ t('button_edit') }}</span>
        </button>

        <button class="md-pop-btn m-btn" @click.stop="deleteChatModal">
          <IconTrash class="icon" />
          <span>{{ t('button_delete') }}</span>
        </button>
      </DropdownEdit>
    </Teleport>
  </article>
</template>

<script setup lang="ts">
import { deleteCall } from '@/api/apiService';
import DownloadTableModal from '@/components/domains/chat/script-download/ChatScriptDownloadModal.vue';
import IconFavorite from '@/components/jh-app/icons/IconFavorite.vue';
import IconModify from '@/components/jh-app/icons/IconModify.vue';
import IconTrash from '@/components/jh-app/icons/IconTrash.vue';
import SelfServiceChatEdit from '@/components/self-service/chat/SelfServiceChatEdit.vue';
import DropdownEdit from '@/components/shared/base/DropdownEdit.vue';
import InterpretingBadge from '@/components/shared/base/InterpretingBadge.vue';
import ModalMedium from '@/components/shared/composite/ModalMedium.vue';
import ModalShareChat from '@/components/shared/composite/ModalShareChat.vue';
import { useAlertStore } from '@/stores/alert';
import { useToastStore } from '@/stores/toast';
import { UserType, useUserStore } from '@/stores/user';
import { IResponseContractCustomerPricing, IResponseMultiCallsDashboard } from '@/types/response';
import { formatSimpleTimeAgo } from '@/utils/dateFormatter';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import { formatNumberWithComma } from '@/utils/stringUtils';

const props = withDefaults(
  defineProps<{
    callInfo: IResponseMultiCallsDashboard;
    unitPriceList: IResponseContractCustomerPricing[];
    usingActions?: boolean;
  }>(),
  {
    callInfo: () => ({}) as IResponseMultiCallsDashboard,
    unitPriceList: () => ({}) as IResponseContractCustomerPricing,
    usingActions: false,
  }
);

const emit = defineEmits<{
  open: [];
  refresh: [];
  select: [callInfo: IResponseMultiCallsDashboard];
  'toggle-favorite': [id: IResponseMultiCallsDashboard];
}>();

const { t } = useI18n();

const alertStore = useAlertStore();
const toastStore = useToastStore();
const userStore = useUserStore();

const refButtonEdit = useTemplateRef('ref-button-edit');
const isOpenDropdownEdit = ref(false);
const activeModalName = ref<string>('');

const callTypeInfo = computed<Record<string, string>>(() => {
  return (
    {
      GENERAL: {
        label: t('label_general'),
        type: 'info-light general',
      },
      CONFERENCE: {
        label: t('label_conference'),
        type: 'info-light conference',
      },
      MEETING: {
        label: t('label_meeting'),
        type: 'info-light meeting',
      },
      SINGLE: {
        label: t('label_single'),
        type: 'success-light',
      },
      AUDIENCE: {
        label: t('label_audience'),
        type: 'gray-light',
      },
    }[props.callInfo.callType] || { label: '', type: 'gray-light' }
  );
});

const chatLogoImageUrl = computed<string>(() => {
  return props.callInfo.callLogoImageUrl ?? getAssetImageUrl('common/logos/logo-sconai.png');
});

const chatTypeLabel = computed(
  () => `${callTypeInfo.value.label} ${formatNumberWithComma(props.callInfo.limitCount)}${t('label_person_count')}`
);

const displayedTime = computed<string>(() => {
  if (!props.callInfo.eventTime) {
    return '';
  }

  const [time, type] = formatSimpleTimeAgo(props.callInfo.eventTime);
  return `${time}${t(type)} ${t('label_before')}`;
});

const isHistoryDownloadEnabled = computed<boolean>(() => {
  const { storeAgreement, userType } = userStore.userInfo;
  return storeAgreement && UserType.isPartner(userType);
});

const handleActiveModalName = (name?: string) => {
  activeModalName.value = name ?? '';
};

const deleteChatModal = () => {
  const { code = '' } = props.callInfo;

  alertStore.confirm(
    {
      title: t('button_delete'),
      message: t('alert_confirm_delete'),
      subMessage: t('alert_delete_irreversible'),
    },
    {
      confirm: async () => {
        const response = await deleteCall(code);
        if (response.success) {
          emit('refresh');
          toastStore.info(t('alert_deleted'));
        }
      },
    }
  );
};

const refreshAfterRequest = () => {
  activeModalName.value = '';
  emit('refresh');
};

const openDropdownEdit = () => {
  isOpenDropdownEdit.value = true;
};

const closeDropdownEdit = () => {
  isOpenDropdownEdit.value = false;
};
</script>

<style scoped lang="scss">
article {
  z-index: $z-card;
}

.itp-used-list {
  padding: 1rem 0;
  transition:
    transform 0.2s,
    background-color 0.1s;
  cursor: pointer;
  border-bottom: 1px solid $color-gray-40;
  &:hover {
    transform: scale(0.98);
  }
  &:active {
    transform: scale(0.98);
  }
  .col-wrap {
    display: flex;
    gap: $spacing2;
    .left-col {
      flex: 0;
      display: flex;
      align-items: center;
      gap: $size-s;
      .logo-box {
        min-width: 48px;
        min-height: 48px;
        width: 48px;
        height: 48px;
        border-radius: 1rem;
        border: 1px solid $color-gray-40;
        overflow: hidden;
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
      .room-info {
        .inr {
          display: flex;
          align-items: center;
          gap: $size-s;
          margin-bottom: 2px;
          div {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: $size-xs;
            .personnal {
              color: $color-gray-200;
              font-size: $font-size-xs;
            }
          }
        }
        .tit {
          flex: 1;
          font-weight: 600;
        }
      }
    }
    .right-col {
      flex: 1;
    }
  }
  .col-btn-wrap {
    position: relative;
    background: $color-gray-10;
    display: flex;
    justify-content: space-between;
    padding: $spacing1;
    border-radius: $spacing2;
    z-index: 10;
    margin-top: $spacing2;
    .left-bw {
      display: flex;
      align-items: center;
      gap: $spacing2;
      .line {
        height: 10px;
        width: 1px;
        background: $color-gray-50;
      }
    }
    button {
      font-size: $font-size-psmall;
      padding: $spacing2;
      cursor: pointer;
    }
  }
}
.bottom-col {
  .meta-title {
    display: flex;
    flex-direction: column;
    width: fit-content;
    gap: 0.5rem;
    .tit {
      font-weight: 500;
    }
  }
  .date {
    font-size: $font-size-psmall;
  }
}

.icon-dim.favorited {
  fill: var(--primary) !important;
}
.md-pop-btn {
  justify-content: flex-start;
  padding: 0.5rem;
  min-width: 120px;
  &:hover {
    background: $gray10;
  }
}

.modify-modal {
  :deep(main) {
    padding: 0;
    display: block;
  }
}
</style>
